/**
 * Email Service for Lavvissa
 *
 * Sends emails via a Supabase Edge Function that wraps Resend.
 * NEVER call Resend directly from the browser — the API key lives server-side.
 *
 * Edge function endpoint: /functions/v1/send-email
 * Expected payload: { to, subject, html, replyTo? }
 */

import { supabase } from '../supabase';
import {
  orderConfirmation,
  shippingUpdate,
  walletCharged,
  walletRejected,
  passwordReset,
  welcomeEmail,
  marketingEmail,
} from './emailTemplates';

const FROM_EMAIL = 'Lavvissa <noreply@lavvissa.com>';

// ── Core send function ─────────────────────────────────────────────

async function sendEmail({ to, subject, html, replyTo }) {
  try {
    const { data, error } = await supabase.functions.invoke('send-email', {
      body: {
        from: FROM_EMAIL,
        to,
        subject,
        html,
        ...(replyTo ? { replyTo } : {}),
      },
    });

    if (error) {
      console.error('[EmailService] Edge function error:', error);
      return { success: false, error: error.message };
    }

    return { success: true, data };
  } catch (err) {
    console.error('[EmailService] Failed to send email:', err);
    return { success: false, error: err.message };
  }
}

// ── Public API ─────────────────────────────────────────────────────

/**
 * Send order confirmation email after checkout.
 */
export async function sendOrderConfirmation(order, user) {
  const email = user?.email;
  if (!email) return { success: false, error: 'No user email' };

  const { subject, html } = orderConfirmation(order, user);
  return sendEmail({ to: email, subject, html });
}

/**
 * Send shipping update with tracking info.
 */
export async function sendShippingUpdate(order, trackingInfo, user) {
  const email = user?.email;
  if (!email) return { success: false, error: 'No user email' };

  const { subject, html } = shippingUpdate(order, trackingInfo, user);
  return sendEmail({ to: email, subject, html });
}

/**
 * Send wallet charge approved notification.
 */
export async function sendWalletCharged(amount, user) {
  const email = user?.email;
  if (!email) return { success: false, error: 'No user email' };

  const { subject, html } = walletCharged(amount, user);
  return sendEmail({ to: email, subject, html });
}

/**
 * Send wallet charge rejected notification.
 */
export async function sendWalletRejected(amount, reason, user) {
  const email = user?.email;
  if (!email) return { success: false, error: 'No user email' };

  const { subject, html } = walletRejected(amount, reason, user);
  return sendEmail({ to: email, subject, html });
}

/**
 * Send password reset email.
 * Note: Supabase handles its own password reset emails, but this can
 * be used as a branded alternative when using custom reset flow.
 */
export async function sendPasswordReset(resetLink, user) {
  const email = user?.email;
  if (!email) return { success: false, error: 'No user email' };

  const { subject, html } = passwordReset(resetLink, user);
  return sendEmail({ to: email, subject, html });
}

/**
 * Send welcome email after signup.
 */
export async function sendWelcomeEmail(user) {
  const email = user?.email;
  if (!email) return { success: false, error: 'No user email' };

  const { subject, html } = welcomeEmail(user);
  return sendEmail({ to: email, subject, html });
}

/**
 * Send marketing/campaign email.
 */
export async function sendMarketingEmail(campaign, user) {
  const email = user?.email;
  if (!email) return { success: false, error: 'No user email' };

  const { subject, html } = marketingEmail(campaign, user);
  return sendEmail({ to: email, subject, html });
}

/**
 * Send marketing email to a list of users (batch).
 * Returns array of results.
 */
export async function sendMarketingBatch(campaign, users) {
  const results = [];
  for (const user of users) {
    const result = await sendMarketingEmail(campaign, user);
    results.push({ email: user.email, ...result });
  }
  return results;
}
